import styled, { css } from 'styled-components';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { usePrevious } from 'hooks';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { storeLocalValue, restoreLocalValue } from '_helpers/storage';

import {
  refreshSearch,
  refreshPage,
  resetSearch,
  startSearch,
} from 'redux/search/slice';

import {
  selectSearch,
  selectPage,
  selectData,
  selectError,
  selectLoading,
} from 'redux/search/selector';

import SearchProcessSectionWithoutResult from 'components/composed/sections/SearchProcessSectionWithoutResult';

const { devices } = theme;

const StyledSearchPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

const SearchPage = ({ path }) => {
  const router = useRouter();
  const text = router.query['text'];
  const dispatch = useDispatch();
  const [searchStarted, setSearchStarted] = useState(false);
  const search = useSelector(selectSearch);
  const page = useSelector(selectPage);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const items = useSelector(selectData);

  const elRef = useRef();

  const prevPage = usePrevious(page);
  const prevText = usePrevious(text);

  const handleChangeSearch = useCallback(
    (search) => {
      dispatch(refreshSearch(search));
    },
    [dispatch]
  );

  const handleChangePage = useCallback(
    (page) => {
      dispatch(refreshPage(page));
    },
    [dispatch]
  );

  const handleChange = useCallback(() => {
    router.push(
      {
        pathname: router.pathname,
        query: { text: search.trim() },
      },
      undefined,
      { shallow: true }
    );
    dispatch(refreshPage('1'));
    dispatch(startSearch({ name: search.trim(), page: '1', count: 10 }));

    if (search.trim()) {
      setSearchStarted(true);
    }
  }, [dispatch, search, router]);

  const clearSearch = useCallback(() => {
    dispatch(resetSearch());
  }, [dispatch]);

  useEffect(() => {
    if (prevText !== text && text) {
      dispatch(
        refreshSearch(
          decodeURI(text?.replace(/%(?![0-9][0-9a-fA-F]+)/g, '%25'))
        )
      );
      dispatch(startSearch({ name: text, page: page, count: 10 }));

      const freeSearchString = sessionStorage.getItem('freeSearchString');

      if (freeSearchString !== null && freeSearchString !== text) {
        setSearchStarted(true);
      }
    }
  }, [dispatch, prevText, text, page]);

  useEffect(() => {
    if (!text) {
      dispatch(resetSearch());
      sessionStorage.removeItem('freeSearchString');
    }
  }, [dispatch, text]);

  useEffect(() => {
    if (
      items?.elements?.length &&
      prevText === text &&
      text &&
      prevPage !== page
    ) {
      dispatch(startSearch({ name: search, page: page, count: 10 }));
    }
  }, [dispatch, prevPage, page, prevText, text, search, items]);

  useEffect(() => {
    if (items?.elements && items?.elements.length === 0 && !loading) {
      if (searchStarted) {
        setSearchStarted(false);
        const product = restoreLocalValue('product');

        if (product) {
          storeLocalValue('product', { ...product, markDesc: search });
        }

        sessionStorage.setItem('freeSearchString', search);
        router.push(path, undefined, {
          shallow: true,
        });
      }
    }

    if (items?.elements && items?.elements.length !== 0 && !loading) {
      sessionStorage.removeItem('freeSearchString');
    }
  }, [items, loading, searchStarted, search, router]);

  useEffect(() => {
    if (elRef.current && items?.elements && items?.elements.length !== 0) {
      window.scrollTo({
        top: elRef.current.offsetTop - 100,
      });
    }
  }, [items]);

  useEffect(() => {
    if (elRef.current && loading) {
      window.scrollTo({
        top: elRef.current.offsetTop - 100,
      });
    }
  }, [loading]);

  useEffect(() => {
    if (elRef.current) {
      window.scrollTo({
        top: elRef.current.offsetTop - 100,
      });
    }
  }, []);

  return (
    <StyledSearchPage>
      <SearchProcessSectionWithoutResult
        loading={loading}
        error={error}
        items={items}
        search={search}
        setSearch={handleChangeSearch}
        page={page}
        setPage={handleChangePage}
        handleChange={handleChange}
        elRef={elRef}
        path={path}
      />
    </StyledSearchPage>
  );
};

export default SearchPage;
